/**
 * Shipping Types
 * Types for shipping rate management with DESI-based pricing
 * Supports multiple carriers per route with priority/cheapest selection
 */

// DESI breakpoints for shipping rates
export const DESI_BREAKPOINTS = [
  0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5,
  5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
  11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  21, 22, 23, 24, 25, 26, 27, 28, 29, 30
] as const;

export type DesiBreakpoint = typeof DESI_BREAKPOINTS[number];

// Shipping route identifiers (origin_destination format)
export type ShippingRouteId =
  | 'TR_US'   // Turkey to United States
  | 'US_US'   // US domestic (for US warehouse)
  | 'TR_UK'   // Turkey to United Kingdom
  | 'TR_CA'   // Turkey to Canada
  | 'TR_EU'   // Turkey to Europe
  | 'TR_AU'   // Turkey to Australia
  | 'TR_UAE'  // Turkey to UAE
  | 'TR_TR'   // Turkey domestic
  | 'TR_SG'   // Turkey to Singapore
  | 'TR_SA';  // Turkey to Saudi Arabia

// Route display configuration
export interface ShippingRouteInfo {
  id: ShippingRouteId;
  name: string;
  shortName: string;      // Short display name
  origin: string;         // Origin country code
  destination: string;    // Destination country code
  flag: string;
  defaultCurrency: string;
  currencySymbol: string;
}

// Default route configurations
export const SHIPPING_ROUTES: ShippingRouteInfo[] = [
  { id: 'TR_US', name: 'TR → ABD', shortName: 'US (TR)', origin: 'TR', destination: 'US', flag: '🇺🇸', defaultCurrency: 'USD', currencySymbol: '$' },
  { id: 'US_US', name: 'US → ABD', shortName: 'US (US)', origin: 'US', destination: 'US', flag: '🇺🇸', defaultCurrency: 'USD', currencySymbol: '$' },
  { id: 'TR_UK', name: 'TR → İngiltere', shortName: 'UK', origin: 'TR', destination: 'UK', flag: '🇬🇧', defaultCurrency: 'GBP', currencySymbol: '£' },
  { id: 'TR_CA', name: 'TR → Kanada', shortName: 'CA', origin: 'TR', destination: 'CA', flag: '🇨🇦', defaultCurrency: 'CAD', currencySymbol: 'C$' },
  { id: 'TR_EU', name: 'TR → Avrupa', shortName: 'EU', origin: 'TR', destination: 'EU', flag: '🇪🇺', defaultCurrency: 'EUR', currencySymbol: '€' },
  { id: 'TR_AU', name: 'TR → Avustralya', shortName: 'AU', origin: 'TR', destination: 'AU', flag: '🇦🇺', defaultCurrency: 'AUD', currencySymbol: 'A$' },
  { id: 'TR_UAE', name: 'TR → BAE', shortName: 'UAE', origin: 'TR', destination: 'UAE', flag: '🇦🇪', defaultCurrency: 'USD', currencySymbol: '$' },
  { id: 'TR_TR', name: 'TR Yurtiçi', shortName: 'TR', origin: 'TR', destination: 'TR', flag: '🇹🇷', defaultCurrency: 'TRY', currencySymbol: '₺' },
  { id: 'TR_SG', name: 'TR → Singapur', shortName: 'SG', origin: 'TR', destination: 'SG', flag: '🇸🇬', defaultCurrency: 'SGD', currencySymbol: 'S$' },
  { id: 'TR_SA', name: 'TR → S.Arabistan', shortName: 'SA', origin: 'TR', destination: 'SA', flag: '🇸🇦', defaultCurrency: 'SAR', currencySymbol: '﷼' },
];

// Shipping carrier/provider (e.g., DHL, FedEx, UPS, Aras, etc.)
export interface ShippingCarrier {
  id: string;                       // UUID
  name: string;                     // Carrier name (e.g., "DHL Express")
  shortName: string;                // Short code (e.g., "DHL")
  color: string;                    // Brand color for UI
  currency: string;                 // Currency for rates (USD, TRY, EUR, etc.)
  isActive: boolean;                // Is carrier active
  website?: string;                 // Carrier website
  notes?: string;                   // Notes about this carrier
  enabledRoutes?: string[];         // List of enabled route IDs (if undefined, all routes enabled)

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

// Custom shipping route (user-defined routes like TR_BR for Brazil)
export interface CustomShippingRoute {
  id: string;                       // UUID (also serves as route ID)
  routeId: string;                  // Unique route identifier (e.g., "TR_BR")
  name: string;                     // Display name (e.g., "TR → Brezilya")
  shortName: string;                // Short name for column header (e.g., "BR")
  origin: string;                   // Origin country code
  destination: string;              // Destination country code
  flag: string;                     // Flag emoji for UI
  defaultCurrency: string;          // Default currency (e.g., "USD")
  currencySymbol: string;           // Currency symbol (e.g., "$")
  isActive: boolean;                // Is route active
  isDefault: boolean;               // Is this a system default route

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

// Carrier rates for a specific route
export interface CarrierRouteRates {
  id: string;                       // UUID
  carrierId: string;                // ShippingCarrier.id
  routeId: ShippingRouteId;         // Destination route
  currency: string;                 // Currency for rates (USD, EUR, etc.)

  // DESI-based rates (desi value -> shipping cost)
  rates: Record<number, number>;    // e.g., { 0.5: 12.5, 1: 15, ... }

  // Additional costs
  additionalPerDesi?: number;       // Cost per DESI above max breakpoint
  minCharge?: number;               // Minimum shipping charge

  // Validity
  validFrom?: Date;                 // Rate valid from date
  validUntil?: Date;                // Rate valid until date

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

// Route shipping configuration (which carrier to use)
export interface RouteShippingConfig {
  id: string;                       // UUID
  routeId: ShippingRouteId;         // Route identifier

  // Selection mode
  selectionMode: 'cheapest' | 'priority';  // Auto-select cheapest or use priority carrier

  // Priority carrier (used when selectionMode is 'priority')
  priorityCarrierId?: string;       // ShippingCarrier.id

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

// Computed rate result (after applying selection logic)
export interface ComputedShippingRate {
  routeId: ShippingRouteId;
  desi: number;
  carrierId: string;
  carrierName: string;
  rate: number;
  currency: string;
  selectionReason: 'cheapest' | 'priority' | 'only_option';
}

// Import/Export format for bulk upload (Excel)
export interface ShippingRateImportRow {
  desi: number;
  [routeId: string]: number | string;  // Route columns: "US", "UK", etc.
}

// Default carriers
export const DEFAULT_CARRIERS: Omit<ShippingCarrier, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'DHL Express',
    shortName: 'DHL',
    color: '#D40511',
    currency: 'USD',
    isActive: true,
    website: 'https://www.dhl.com',
  },
  {
    name: 'FedEx International',
    shortName: 'FedEx',
    color: '#4D148C',
    currency: 'USD',
    isActive: true,
    website: 'https://www.fedex.com',
  },
  {
    name: 'UPS Worldwide',
    shortName: 'UPS',
    color: '#351C15',
    currency: 'USD',
    isActive: true,
    website: 'https://www.ups.com',
  },
  {
    name: 'Aras Kargo',
    shortName: 'Aras',
    color: '#00A651',
    currency: 'TRY',
    isActive: true,
    website: 'https://www.araskargo.com.tr',
  },
  {
    name: 'PTT Kargo',
    shortName: 'PTT',
    color: '#FFD100',
    currency: 'TRY',
    isActive: true,
    website: 'https://www.ptt.gov.tr',
  },
];
